import { PropertyListings } from "@/components/property/property-listings";
import { propertyMedia, caldwellHeroMedia } from '@/lib/property-media';
import { PropertyMediaHero } from '@/components/property/property-media';
import { useEffect, useState } from "react";
import { ClickableCard } from "@/components/property/clickable-card";
import { LastMinuteOffer } from "@/components/promotions/last-minute-offer";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useLocation } from "wouter";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { PageHero } from "@/components/layout/page-hero";
import { wavesVideo, wavesPoster } from "@/lib/media-assets";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import { BookingWithOffers } from "@/components/promotions/booking-with-offers";

import { WhyBookDirect } from "@/components/booking/why-book-direct";
import { BookingOfferPopup } from "@/components/booking/booking-offer-popup";
import { PhotoGallery } from "@/components/property/photo-gallery";
import { AmenitiesGrid } from "@/components/property/amenities-grid";
import { LocationMap } from "@/components/property/location-map";
import ReviewsSection from "@/components/property/reviews-section";
import { FaqSection } from "@/components/seo/faq-section";
import { SectionHeading } from "@/components/ui/section-heading";
import { SectionNav } from "@/components/property/section-nav";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { Button } from "@/components/ui/button";
import { Star, Shield, Calendar, Droplets, BadgeCheck } from "lucide-react";
import { useSEO, generateBreadcrumbSchema, generateFAQSchema } from "@/lib/seo-utils";
import { useBooking } from "@/hooks/use-booking-context";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { formatTimeToStandard } from "@/lib/time-utils";
import type { PublicProperty } from "@shared/schema";
import { BOOK_DIRECT_SAVINGS, QUIET_HOURS_START, QUIET_HOURS_END } from "@shared/property-facts";
import StayPage from "@/pages/stay-page";
import { HomeComparison } from "@/components/property/home-comparison";
import { OtherHomeSwitch } from "@/components/property/other-home-switch";
import { RateLine } from "@/components/property/rate-line";
import { TrustLine } from "@/components/property/trust-line";
import { NextOpenWindows } from "@/components/property/next-open-windows";
import { NewListingBadge } from "@/components/property/new-listing-badge";
import { KUALOA_PROPERTY_SLUG, KUALOA_STAY_PATH } from "@shared/property-context";

type ListedProperty = PublicProperty & {
  booking_enabled?: boolean;
  quiet_hours_start?: string | null;
  quiet_hours_end?: string | null;
  house_rules?: string | null;
};

/** The image optimizer serves any stored photo resized and as WebP. */
const img = (src: string, w: number) => `/img?src=${encodeURIComponent(src)}&w=${w}&q=90`;

/** Blank-line separated text into paragraphs; a single block stays one paragraph. */
function paragraphs(text: string | null | undefined): string[] {
  return (text || "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function coverOf(property: ListedProperty): string | undefined {
  const first = [...(property.photos || [])].sort((a, b) => a.sort_order - b.sort_order)[0];
  return property.hero_image_url || first?.url || undefined;
}

export default function PropertyPage() {
  const params = useParams<{ slug: string }>();
  const slug = params.slug || "";
  const { setProperty } = useBooking();

  const { data: property, isLoading, isError } = useQuery<ListedProperty>({
    queryKey: ["/api/properties", slug],
    enabled: Boolean(slug),
  });

  useEffect(() => {
    if (property?.id && property.slug) {
      setProperty(property.id, property.slug);
    }
  }, [property?.id, property?.slug, setProperty]);

  const title = property?.title || "";
  const locationLabel = property?.public_location_label || property?.general_location || "Oahu";
  const cover = property ? coverOf(property) : undefined;

  const propertyFaqs = property
    ? [
        {
          question: `Where is ${title} located?`,
          answer: `${title} is in ${locationLabel}, on Oahu's windward coast, about 23 miles (30 minutes) from Honolulu airport. Book direct through Vacation Rental Oahu for the best rate.`,
        },
        {
          question: `How many guests can stay at ${title}?`,
          answer: `The home sleeps up to ${property.max_guests} guests across ${property.bedrooms} bedrooms and ${property.bathrooms} bathrooms.`,
        },
        {
          question: `What time is check-in and checkout at ${title}?`,
          answer: `Check-in is after ${formatTimeToStandard(property.check_in_time) || "3:00 PM"} and checkout is before ${formatTimeToStandard(property.check_out_time) || "10:00 AM"}.`,
        },
        {
          question: "Why book direct instead of through a listing site?",
          answer: `${BOOK_DIRECT_SAVINGS} Payments are processed securely through Stripe, and you receive instant confirmation from the property.`,
        },
      ]
    : [];

  const seoHead = useSEO({
    title: property ? `${title} | Vacation Rental Oahu` : "Vacation rental | Vacation Rental Oahu",
    description: property?.short_description || property?.marketing_description?.slice(0, 160) || "A distinct Oahu vacation home you can book direct.",
    canonical: property?.slug ? `https://vacationrentaloahu.co/properties/${property.slug}` : undefined,
    keywords: `${title || "Oahu vacation rental"}, ${locationLabel} vacation home, book direct`,
    ogTitle: property ? `${title} | Vacation Rental Oahu` : undefined,
    ogDescription: property?.short_description || undefined,
    ogImage: cover ? `https://vacationrentaloahu.co${img(cover, 1200)}` : undefined,
    structuredData: property
      ? [
          generateBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Our Homes", url: "/properties" },
            { name: title, url: `/properties/${property.slug}` },
          ]),
          generateFAQSchema(propertyFaqs),
        ]
      : [],
  });

  // The original home keeps its own page; everything else renders the shared listing.
  if (slug === KUALOA_PROPERTY_SLUG) {
    return <StayPage />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-shell">
        <Header />
        <main className="mx-auto max-w-5xl px-4 py-16">
          <p className="font-sans text-luxury-sm text-ink/70">Loading this home...</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (isError || !property) {
    return (
      <div className="min-h-screen bg-shell">
        <Header />
        <main className="mx-auto max-w-5xl px-4 py-16">
          <h1 className="font-serif text-3xl text-ink">This home is not available</h1>
          <p className="mt-4 font-sans text-ink/70">
            The listing you requested is hidden or does not exist. Browse our published homes instead.
          </p>
          <Link href="/properties" className="mt-6 inline-block font-sans text-sun underline">
            View our homes
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const canBook = property.booking_enabled !== false;
  const body = paragraphs(property.marketing_description || property.description);
  const reviewCount = Number(property.review_count || 0);
  const rating = property.rating ? Number(property.rating) : null;
  const quietStart = property.quiet_hours_start || QUIET_HOURS_START;
  const quietEnd = property.quiet_hours_end || QUIET_HOURS_END;

  return (
    <div className="min-h-screen bg-shell">
      {seoHead}
      <Header />
      <main id="main-content" className="has-section-nav">
        {propertyMedia(property.slug) ? <PropertyMediaHero media={property.slug === 'caldwell-beach-house' ? caldwellHeroMedia : propertyMedia(property.slug)!} title={title} location={locationLabel} /> : (
        <PageHero
          title={<span data-testid="property-title">{title}</span>}
          subtitle={property.short_description || locationLabel}
          eyebrow="The Property"
          videoSrc={property.hero_video_url || undefined}
          imageSrc={cover}
          posterSrc={cover}
          imageAlt={`${title}, ${locationLabel}`}
        />
        )}

        <section className="bg-shell border-b border-sand-deep">
          <div className="container mx-auto px-4 md:px-6 py-6 md:py-8 max-w-6xl">
            <Breadcrumbs
              items={[
                { name: "Home", url: "/" },
                { name: "Our Homes", url: "/properties" },
                { name: title, url: `/properties/${property.slug}` },
              ]}
              className="mb-5"
            />
            <div className="flex flex-wrap items-center gap-3 text-luxury-sm">
              <div className="flex items-center">
                <Star className="h-4 w-4 text-sun fill-current mr-1" aria-hidden="true" />
                <span className="font-serif font-normal mr-1 text-ink" data-testid="property-rating">
                  {reviewCount > 0 && rating ? rating.toFixed(2) : "New"}
                </span>
                <span className="text-ink-soft">{reviewCount > 0 ? `(${reviewCount} reviews)` : "listing"}</span>
              </div>
              <span className="text-sand-deep" aria-hidden="true">•</span>
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-driftwood">Entire home</span>
              <span className="text-sand-deep" aria-hidden="true">•</span>
              <span className="text-ink-soft" data-testid="property-location">{locationLabel}</span>
            </div>
          </div>
        </section>

        <SectionNav
          items={[
            { id: "photos", label: "Photos" },
            { id: "overview", label: "Overview" },
            { id: "amenities", label: "Amenities" },
            { id: "location", label: "Location" },
            ...(reviewCount > 0 ? [{ id: "reviews", label: "Reviews" }] : []),
            { id: "faq", label: "FAQ" },
            { id: "book", label: "Book" },
          ]}
        />

        <div id="photos">
          <PhotoGallery
            propertySlug={property.slug || undefined}
            heading={`${title}: the photo tour`}
            lede={`From the water's edge through every room of the house, in the order you would walk it.`}
          />
        </div>


        <section id="overview" className="pt-6 pb-12 md:py-16 bg-shell section-stable border-t border-sand-deep">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
                <div className="lg:col-span-2 order-2 lg:order-1">
                  {/* Overview */}
                  <ScrollReveal className="mb-10">
                    <SectionHeading
                      align="left"
                      eyebrow="Entire Home"
                      title={`${title} in ${locationLabel}`}
                      className="mb-2"
                    />
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-ink-soft text-luxury-sm font-sans tracking-wide">
                      <span data-testid="property-guests-overview">{property.max_guests} GUESTS</span>
                      <span className="text-sand-deep">•</span>
                      <span data-testid="property-bedrooms-overview">{property.bedrooms} BEDROOMS</span>
                      <span className="text-sand-deep">•</span>
                      <span data-testid="property-bathrooms-overview">{property.bathrooms} BATHROOMS</span>
                    </div>
                    {property.short_description && (
                      <p className="text-ink-soft leading-relaxed mt-6 max-w-3xl text-luxury-lg font-light">
                        {property.short_description}
                      </p>
                    )}
                  </ScrollReveal>

                  {/* Key features: only claims that hold for every home we operate */}
                  <ScrollReveal className="property-editorial-grid mb-12">
                    <div className="flex items-start space-x-5">
                      <div className="w-10 h-10 rounded-full bg-palm/10 flex items-center justify-center flex-shrink-0">
                        <Shield className="h-5 w-5 text-palm stroke-[1.5]" />
                      </div>
                      <div>
                        <h3 className="text-luxury-lg font-serif font-normal text-forest mb-1 tracking-tight">Entire home to yourselves</h3>
                        <p className="text-ink-soft text-luxury-sm font-light leading-relaxed">
                          The whole house, its grounds and its views are yours for the stay. No shared spaces.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-5">
                      <div className="w-10 h-10 rounded-full bg-palm/10 flex items-center justify-center flex-shrink-0">
                        <Droplets className="h-5 w-5 text-palm stroke-[1.5]" />
                      </div>
                      <div>
                        <h3 className="text-luxury-lg font-serif font-normal text-forest mb-1 tracking-tight">Enhanced Clean</h3>
                        <p className="text-ink-soft text-luxury-sm font-light leading-relaxed">
                          Professional 5-step enhanced cleaning process between every stay.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-5">
                      <div className="w-10 h-10 rounded-full bg-palm/10 flex items-center justify-center flex-shrink-0">
                        <BadgeCheck className="h-5 w-5 text-palm stroke-[1.5]" />
                      </div>
                      <div>
                        <h3 className="text-luxury-lg font-serif font-normal text-forest mb-1 tracking-tight">Book direct</h3>
                        <p className="text-ink-soft text-luxury-sm font-light leading-relaxed">
                          {BOOK_DIRECT_SAVINGS} Secure payment through Stripe and instant confirmation.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-5">
                      <div className="w-10 h-10 rounded-full bg-palm/10 flex items-center justify-center flex-shrink-0">
                        <Calendar className="h-5 w-5 text-palm stroke-[1.5]" />
                      </div>
                      <div>
                        <h3 className="text-luxury-lg font-serif font-normal text-forest mb-1 tracking-tight">Cancellation policy</h3>
                        <ul className="text-ink-soft text-luxury-sm font-light leading-relaxed space-y-0.5 list-none">
                          <li>Full refund if cancelled 30 or more days before check-in</li>
                          <li>50% refund of nightly total 7 to 30 days before check-in</li>
                          <li>No nightly refund within 7 days; cleaning fee always refunded</li>
                        </ul>
                        <a href="/policies" className="text-luxury-xs text-driftwood hover:text-ink underline underline-offset-2 mt-1 inline-flex min-h-[44px] items-center font-sans">Full policy</a>
                      </div>
                    </div>
                  </ScrollReveal>

                  {/* Description */}
                  <ScrollReveal className="mb-12 pb-12 border-b border-sand-deep">
                    <SectionHeading
                      align="left"
                      eyebrow="The Experience"
                      title={`About ${title}`}
                      className="mb-6"
                    />
                    <div className="space-y-5 text-ink-soft leading-relaxed font-light text-luxury-base" data-testid="property-description">
                      {body.map((p, i) => (
                        <p key={i}>{p}</p>
                      ))}
                    </div>
                  </ScrollReveal>

                  {/* House rules */}
                  <ScrollReveal className="mb-10">
                    <SectionHeading align="left" eyebrow="Details" title="House rules" className="mb-8" />
                    <div className="grid md:grid-cols-3 gap-6 mb-8 border border-sand-deep rounded p-6 bg-shell">
                      <div>
                        <h3 className="text-luxury-sm font-sans tracking-widest text-driftwood uppercase mb-2">Check-in</h3>
                        <p className="text-ink font-serif text-luxury-lg">After {formatTimeToStandard(property.check_in_time) || "3:00 PM"}</p>
                      </div>
                      <div>
                        <h3 className="text-luxury-sm font-sans tracking-widest text-driftwood uppercase mb-2">Check-out</h3>
                        <p className="text-ink font-serif text-luxury-lg">Before {formatTimeToStandard(property.check_out_time) || "10:00 AM"}</p>
                      </div>
                      <div>
                        <h3 className="text-luxury-sm font-sans tracking-widest text-driftwood uppercase mb-2">Maximum guests</h3>
                        <p className="text-ink font-serif text-luxury-lg">{property.max_guests} guests</p>
                      </div>
                    </div>
                    <div className="space-y-3 text-ink-soft text-luxury-base font-light">
                      <p className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-sand-deep block"></span> No smoking anywhere on the property</p>
                      <p className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-sand-deep block"></span> No events or parties</p>
                      <p className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-sand-deep block"></span> No pets. Pets are not permitted</p>
                      <p className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-sand-deep block"></span> Quiet hours from {quietStart} to {quietEnd}</p>
                    </div>
                    <a href="/house-rules" className="text-luxury-xs text-driftwood hover:text-ink underline underline-offset-2 mt-4 inline-block font-sans">Full house rules</a>
                  </ScrollReveal>
                </div>

                {/* Booking widget: first on mobile so it sits under the gallery */}
                <div className="lg:col-span-1 order-1 lg:order-2">
                  <div id="book" className="sticky top-28 z-20" data-testid="booking-widget-container" data-sticky-book-anchor>
                    {canBook ? (
                      <>
                        <BookingWithOffers propertyId={property?.id} propertySlug={property?.slug ?? undefined} />
                      </>
                    ) : (
                      <div className="booking-form-panel rounded-sm border border-sand-deep p-6">
                        <h2 className="font-serif text-xl text-ink">Bookings are paused</h2>
                        <p className="mt-3 font-sans text-luxury-sm text-ink/70">
                          {title} is visible but not accepting new reservations right now. Inquire and we can help you choose another home.
                        </p>
                        <Link href="/contact" className="mt-4 inline-block font-sans text-sun underline">Contact us</Link>
                      </div>
                    )}
                    <OtherHomeSwitch currentSlug={property?.slug || slug} className="mt-6" />
                    <WhyBookDirect variant="compact" className="mt-6" />
                    <div className="mt-8 p-6 border border-sand-deep rounded text-center bg-shell">
                      <p className="text-luxury-sm text-ink-soft mb-4 tracking-wide">Questions about the property?</p>
                      <Link href="/contact">
                        <Button variant="bronze" size="lg" data-testid="contact-host-button" className="w-full">
                          Contact Host
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <AmenitiesGrid propertySlug={property.slug || undefined} />
        <LocationMap propertySlug={property.slug || undefined} />
        {reviewCount > 0 && (
          <div id="reviews">
            <ReviewsSection propertySlug={property.slug || undefined} />
          </div>
        )}

        <div id="faq">
          <ScrollReveal>
            <FaqSection eyebrow="Help & Info" title="Property FAQ" items={propertyFaqs} />
          </ScrollReveal>
        </div>
      </main>
      <Footer />
      <StickyMobileBookCta />
      <BookingOfferPopup autoTrigger={false} />
    </div>
  );
}

/**
 * The one-line blurb on a directory card. The short description when the home
 * has one, otherwise the opening sentence of its listing text, so every card
 * carries a blurb of similar length and the price rows line up.
 */

export function PropertyDirectoryPage() {
  const flags = useFeatureFlags();
  const [, setLocation] = useLocation();
  const [showCompare, setShowCompare] = useState(
    () => typeof window !== "undefined" && window.location.hash === "#compare",
  );
  useEffect(() => {
    const onHash = () => { if (window.location.hash === "#compare") setShowCompare(true); };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const { data: properties = [], isLoading } = useQuery<ListedProperty[]>({
    queryKey: ["/api/properties"],
  });

  useEffect(() => {
    if (isLoading) return;
    if (properties.length > 1) return;
    const only = properties[0];
    if (only?.slug) {
      setLocation(`/properties/${only.slug}`);
      return;
    }
    setLocation(flags.propertyRoutes ? `/properties/${KUALOA_PROPERTY_SLUG}` : "/stay");
  }, [flags.propertyRoutes, isLoading, properties, setLocation]);

  const directoryFaqs = [
    {
      question: "How do I compare Vacation Rental Oahu homes?",
      answer: "Our Homes shows both homes side by side: sleeps, bedrooms, bathrooms, rates and features in one table, with a link to each listing.",
    },
    {
      question: "Can I book more than one home?",
      answer: "Yes. Each home is reserved separately, so you can book one stay or hold different dates at another listing.",
    },
  ];

  const seoHead = useSEO({
    // Search Console, 16 months to 21 Sep 2026: "beach house rentals oahu"
    // (165 impressions), "oahu beach house" (89), "beach house rental oahu"
    // (68) and a dozen variants all show, none click, and none of them is
    // named on this page. "Our Homes" is the menu label, not what a searcher
    // types.
    title: "Oahu Beach House Rentals: Two Oceanfront Homes in Ka'a'awa",
    description: "Two oceanfront beach houses in Ka'a'awa on Oahu's windward coast, sleeping 8 and 11. Compare rooms, rates and open dates side by side, then book direct with no platform fees.",
    canonical: "https://vacationrentaloahu.co/properties",
    keywords: "oahu vacation homes, windward oahu rentals, vacation rental oahu listings",
    structuredData: [
      generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Our Homes", url: "/properties" },
      ]),
      generateFAQSchema(directoryFaqs),
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Vacation Rental Oahu homes",
        itemListElement: properties.map((home, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `https://vacationrentaloahu.co/properties/${home.slug}`,
          name: home.title,
        })),
      },
    ],
  });

  if (isLoading || properties.length <= 1) {
    return (
      <div className="min-h-screen bg-shell">
        <Header />
        <main className="mx-auto max-w-6xl px-4 py-16">
          <p className="font-sans text-ink/70">Taking you to the available home...</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-shell">
      {seoHead}
      <Header />
      <main id="main-content">
        <PageHero
          eyebrow="Our Homes"
          title="Beachfront homes on windward Oahu"
          subtitle="Oceanfront stays with views of Mokoli'i and the Ko'olau range. Choose the home that fits your group, then book it direct."
          videoSrc={wavesVideo}
          posterSrc={wavesPoster}
          imageAlt="Mokoli'i and the Ko'olau range from the air on Oahu's windward coast"
        />

        <section className="bg-sand border-b border-sand-deep">
          <div className="container mx-auto px-4 md:px-6 py-8 md:py-10 max-w-6xl">
            <Breadcrumbs items={[{ name: "Home", url: "/" }, { name: "Our Homes", url: "/properties" }]} />
          </div>
        </section>

        <section className="container mx-auto px-4 md:px-6 py-12 md:py-16 max-w-6xl">
          <PropertyListings properties={properties} />

          {/* The choice comes first; the long comparison waits behind a
              button, and opens on its own when a link points at #compare. */}
          <div id="compare-homes" className="mt-16 md:mt-20 scroll-mt-28">
            {showCompare ? (
              <ScrollReveal>
                <HomeComparison homes={properties} />
              </ScrollReveal>
            ) : (
              <div className="border border-sand-deep bg-sand px-6 py-8 text-center">
                <p className="font-serif text-[24px] text-forest">Not sure which one?</p>
                <p className="mt-2 font-sans text-luxury-sm text-ink-soft">See the two homes side by side: sleeps, rooms, rates and what each has.</p>
                <Button className="mt-5 bg-ink text-shell hover:bg-ink/90 font-sans tracking-wide" onClick={() => setShowCompare(true)} data-testid="compare-in-detail">
                  Compare in detail
                </Button>
              </div>
            )}
          </div>
        </section>

        <ScrollReveal>
          <FaqSection eyebrow="Help & Info" title="Frequently asked questions" items={directoryFaqs} />
        </ScrollReveal>
      </main>
      <Footer />
      <StickyMobileBookCta />
    </div>
  );
}

export function StayRedirect() {
  const flags = useFeatureFlags();
  const [, setLocation] = useLocation();
  useEffect(() => {
    if (flags.propertyRoutes) {
      setLocation(KUALOA_STAY_PATH);
    }
  }, [flags.propertyRoutes, setLocation]);
  if (flags.propertyRoutes) return null;
  return <StayPage />;
}
